import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import {
  AssetVerification,
  typeguards,
} from '@finley-technologies/finley-types';

import { createAthenaQueryShell } from '../../../../../../athena/ui/AthenaQueryView';
import {
  formatSelectOptions,
  Select,
} from '../../../../../../common-frontend/components/Select';
import {
  CreditAgreement,
  CreditAgreementFullArtifactWipConfigType,
} from '../../../../types';

const getAssetVerificationQueryConfig = (): CreditAgreement.Config.CommonPostgresQuery.Config.AssetVerificationQueryConfig => {
  return {
    statusFilter: {
      type: 'assetVerificationStatusArray',
      statuses: [
        ...AssetVerification.AssetVerificationOutput.ReportEntryStatusIterable,
      ],
    },
  };
};

const getAthenaQueryFileInsertionConfigShell = (
  index: number,
): CreditAgreement.Config.Reporting.Outputs.Docx.AthenaQueryFileInsertionConfig => {
  return {
    fileType: 'athenaQuery',
    reportingDateFormula: 'reportingDate',
    athenaQuery: createAthenaQueryShell(index),
    columns: [],
  };
};

const getPostgresQueryArrayResultShell = (
  index: number,
): CreditAgreement.Config.CommonPostgresQuery.Query.AssetVerificationQuery => {
  return {
    name: `postgresQuery${index}`,
    type: 'assetVerification',
    parameterConfig: getAssetVerificationQueryConfig(),
    resultType: 'array',
    variables: [],
  };
};

const getPostgresQueryFileInsertionConfigShell = (
  index: number,
): CreditAgreement.Config.Reporting.Outputs.Docx.PostgresQueryFileInsertionConfig => {
  return {
    fileType: 'postgresQuery',
    reportingDateFormula: 'reportingDate',
    postgresQuery: getPostgresQueryArrayResultShell(index),
    columns: [],
  };
};

export const updatePostgresQueryArrayResultShell = ({
  query,
}: {
  query: CreditAgreement.Config.Reporting.PostgresQuery.QueryArrayResult;
}): CreditAgreement.Config.Reporting.PostgresQuery.QueryArrayResult => {
  const { type } = query;
  switch (type) {
    case 'assetVerification':
      return {
        ...query,
        parameterConfig: getAssetVerificationQueryConfig(),
      };
    default:
      return finleyAssertUnreachable(type);
  }
};

const getFileInsertionOutputShell = (params: {
  fileType: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionType;
  index: number;
}): CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionOutput => {
  const { fileType, index } = params;
  let config;

  switch (fileType) {
    case 'athenaQuery':
      config = getAthenaQueryFileInsertionConfigShell(index);
      break;
    case 'postgresQuery':
      config = getPostgresQueryFileInsertionConfigShell(index);
      break;
    default:
      throw finleyAssertUnreachable(fileType);
  }

  return {
    name: 'variableName',
    type: 'fileInsertion',
    config,
  };
};

const addOutput = async (params: {
  basePropertyKey: string;
  fileType: CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionType;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
}): Promise<void> => {
  const { basePropertyKey, fileType, wipConfig } = params;
  const outputs = getIn(wipConfig.formProps.values, basePropertyKey) ?? [];

  finleyAssert(
    typeguards.creditAgreement.isDocxFileInsertionOutputs(outputs),
    `Retrieved non-DocxFileInsertionOutputs structure for [key:${basePropertyKey}]`,
  );

  const outputShell = getFileInsertionOutputShell({
    index: outputs.length,
    fileType,
  });
  const outputsCopy = lodash.cloneDeep(outputs);
  outputsCopy.push(outputShell);
  await wipConfig.formProps.setFieldValue(basePropertyKey, outputsCopy);
};

type AddConfigModalProps = {
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
};
export const AddConfigModal = ({
  basePropertyKey,
  isOpen,
  setIsOpen,
  wipConfig,
}: AddConfigModalProps) => {
  const selectionOptions = formatSelectOptions(
    CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionTypeIterable,
    'title',
  );

  const [selectedFileType, setSelectedFileType] = useState<
    CreditAgreement.Config.Reporting.Outputs.Docx.FileInsertionType | undefined
  >(undefined);

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="3xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Add File Insertion Output</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Select
            value={selectedFileType}
            onChange={(configType) => setSelectedFileType(configType)}
            options={selectionOptions}
            placeholderLabel="Select config type"
          />
          <Flex justifyContent="center" mt="8">
            <Button
              onClick={async () => {
                if (selectedFileType) {
                  await addOutput({
                    basePropertyKey,
                    fileType: selectedFileType,
                    wipConfig,
                  });
                }
                setIsOpen(false);
              }}
              aria-label="Done"
              isDisabled={lodash.keys(wipConfig.formProps.errors).length > 0}
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
